// ══════════════════════════════════════════════════════════════════
// Forfaits, taxes et montants (facturation B2G, en cents CAD)
// ══════════════════════════════════════════════════════════════════

import { z } from 'zod';

export const TENANT_PLANS = ['TRIAL', 'STARTER', 'STANDARD', 'PRO', 'ENTERPRISE', 'CUSTOM'] as const;
export type TenantPlanCode = (typeof TENANT_PLANS)[number];

export const TENANT_STATUSES = ['TRIAL', 'ACTIVE', 'PAST_DUE', 'SUSPENDED', 'ARCHIVED'] as const;

/** Domaine des comptes de la ville de démonstration : aucun courriel n'y est jamais envoyé. */
export const DEMO_EMAIL_DOMAIN = 'demo.ci-ville.ca';
export const isDemoEmail = (email?: string | null): boolean => !!email && email.toLowerCase().endsWith(`@${DEMO_EMAIL_DOMAIN}`);
export type TenantStatusCode = (typeof TENANT_STATUSES)[number];

export interface PlanDefinition {
  label: string;
  description: string;
  maxUsers: number;
  /** null = illimité */
  maxReportsPerMonth: number | null;
  /** Prix annuel indicatif hors taxes, en cents (null = sur devis) */
  suggestedAnnualCents: number | null;
}

/**
 * Formules de la grille tarifaire 2026-2027 : un seul abonnement dont le prix dépend de la population, précédé
 * au besoin d'un projet pilote ; les codes restent ceux de la base. Valeurs par défaut appliquées quand le super
 * admin change de formule (modifiables ensuite). Le prix dépend de la population : voir priceGridRow().
 */
export const PLAN_CATALOG: Record<TenantPlanCode, PlanDefinition> = {
  TRIAL: { label: 'Essai', description: '30 jours, pour évaluer la plateforme', maxUsers: 5, maxReportsPerMonth: 300, suggestedAnnualCents: 0 },
  STARTER: { label: 'Projet pilote', description: 'Six mois, toutes les fonctions et l\'accompagnement (75 % de l\'abonnement)', maxUsers: 25, maxReportsPerMonth: null, suggestedAnnualCents: null },
  STANDARD: { label: 'Abonnement', description: 'Toutes les fonctions, prix selon la population', maxUsers: 25, maxReportsPerMonth: null, suggestedAnnualCents: null },
  PRO: { label: 'Abonnement et modules', description: 'Abonnement, plus l\'API d\'intégration ou l\'analyse prédictive (15 % chacun, en préparation)', maxUsers: 50, maxReportsPerMonth: null, suggestedAnnualCents: null },
  ENTERPRISE: { label: 'Grande ville ou regroupement', description: '500 000 habitants et plus, ou MRC ; sur devis', maxUsers: 500, maxReportsPerMonth: null, suggestedAnnualCents: null },
  CUSTOM: { label: 'Sur mesure', description: 'Conditions négociées', maxUsers: 25, maxReportsPerMonth: null, suggestedAnnualCents: null },
};

/** Part de l'abonnement annuel : projet pilote de six mois, services compris. */
export const PILOT_SHARE = 0.75;
/** Rabais de la première année d'abonnement d'une Municipalité fondatrice. */
export const FOUNDER_DISCOUNT = 0.15;
/** Rabais d'un engagement de trois ans (cumulable avec le rabais fondateur). */
export const COMMITMENT_DISCOUNT = 0.05;
/** Rabais d'un regroupement d'au moins trois municipalités d'une même MRC (non cumulable avec le rabais fondateur). */
export const MRC_DISCOUNT = 0.1;
/** Part de l'abonnement annuel : chaque module supplémentaire (API d'intégration, analyse prédictive). */
export const MODULE_SHARE = 0.15;

export interface PriceGridRow {
  /** Libellé du palier de population */
  label: string;
  maxPopulation: number;
  /** Abonnement annuel, toutes les fonctions */
  annualCents: number;
  /** Projet pilote de 6 mois, services compris */
  pilotCents: number;
  /** Première année d'une Municipalité fondatrice */
  founderFirstYearCents: number;
  /** Chaque module supplémentaire, par année */
  moduleCents: number;
  /** Projet pilote d'une Municipalité fondatrice (1re cohorte) */
  founderPilotCents: number;
}

/** Pilote d'une Municipalité fondatrice : 5 500 $ sous 20 000 habitants, 7 500 $ au-delà, jamais plus que le pilote courant. */
export const FOUNDER_PILOT_SMALL_CENTS = 550_000;
export const FOUNDER_PILOT_LARGE_CENTS = 750_000;

const row = (label: string, maxPopulation: number, annualCents: number): PriceGridRow => ({
  label, maxPopulation, annualCents,
  pilotCents: Math.round(annualCents * PILOT_SHARE),
  founderPilotCents: Math.min(Math.round(annualCents * PILOT_SHARE),
    maxPopulation < 20_000 ? FOUNDER_PILOT_SMALL_CENTS : FOUNDER_PILOT_LARGE_CENTS),
  founderFirstYearCents: Math.round(annualCents * (1 - FOUNDER_DISCOUNT)),
  moduleCents: Math.round(annualCents * MODULE_SHARE),
});

/** Grille tarifaire 2026-2027, hors taxes, selon la population (décret en vigueur). Au-delà : sur devis. */
export const PRICE_GRID: PriceGridRow[] = [
  row('Moins de 2 000 habitants', 1_999, 500_000),
  row('2 000 à 4 999 habitants', 4_999, 800_000),
  row('5 000 à 9 999 habitants', 9_999, 1_250_000),
  row('10 000 à 19 999 habitants', 19_999, 1_750_000),
  row('20 000 à 49 999 habitants', 49_999, 2_600_000),
  row('50 000 à 99 999 habitants', 99_999, 4_400_000),
  row('100 000 à 199 999 habitants', 199_999, 6_600_000),
  row('200 000 à 499 999 habitants', 499_999, 10_000_000),
];

/** Palier de la grille pour une population ; null si inconnue ou sur devis (500 000 habitants et plus). */
export function priceGridRow(population?: number | null): PriceGridRow | null {
  if (!population || population < 1) return null;
  return PRICE_GRID.find((r) => population <= r.maxPopulation) ?? null;
}

/** Montant suggéré pour une formule et une population (prix courant, sans rabais ni module). */
export function suggestedAnnualCents(plan: TenantPlanCode, population?: number | null): number | null {
  const r = priceGridRow(population);
  if (!r) return PLAN_CATALOG[plan].suggestedAnnualCents;
  if (plan === 'STARTER') return r.pilotCents;
  if (plan === 'STANDARD' || plan === 'PRO') return r.annualCents;
  return PLAN_CATALOG[plan].suggestedAnnualCents;
}

export const TENANT_STATUS_LABELS_FR: Record<TenantStatusCode, string> = {
  TRIAL: 'Essai',
  ACTIVE: 'Actif',
  PAST_DUE: 'Paiement en retard',
  SUSPENDED: 'Suspendu',
  ARCHIVED: 'Archivé',
};

// ── Taxes du Québec ───────────────────────────────────────────────
export const TPS_RATE = 0.05;
export const TVQ_RATE = 0.09975;

export interface InvoiceLine {
  description: string;
  quantity: number;
  unitPriceCents: number;
}

export interface InvoiceTotals {
  subtotalCents: number;
  tpsCents: number;
  tvqCents: number;
  totalCents: number;
}

/** Arrondi au cent le plus proche, taxes calculées sur le sous-total (pas ligne par ligne). */
export function computeInvoiceTotals(lines: InvoiceLine[], taxExempt = false): InvoiceTotals {
  const subtotalCents = lines.reduce((sum, l) => sum + Math.round(l.quantity * l.unitPriceCents), 0);
  const tpsCents = taxExempt ? 0 : Math.round(subtotalCents * TPS_RATE);
  const tvqCents = taxExempt ? 0 : Math.round(subtotalCents * TVQ_RATE);
  return { subtotalCents, tpsCents, tvqCents, totalCents: subtotalCents + tpsCents + tvqCents };
}

export const FREQUENCY_MONTHS = { ANNUAL: 12, SEMIANNUAL: 6, QUARTERLY: 3, MONTHLY: 1 } as const;
export type BillingFrequencyCode = keyof typeof FREQUENCY_MONTHS;

export const BILLING_FREQUENCY_LABELS_FR: Record<BillingFrequencyCode, string> = {
  ANNUAL: 'Annuelle',
  SEMIANNUAL: 'Semestrielle',
  QUARTERLY: 'Trimestrielle',
  MONTHLY: 'Mensuelle',
};

export const PAYMENT_METHOD_LABELS_FR = {
  CHEQUE: 'Chèque',
  TRANSFER: 'Virement',
  EFT: 'Dépôt direct (TEF)',
  CARD: 'Carte de crédit',
  OTHER: 'Autre',
} as const;

export function formatMoney(cents: number, locale = 'fr-CA'): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency: 'CAD' }).format(cents / 100);
}

/** Paliers d'alerte de renouvellement (jours avant la fin du contrat) */
export const RENEWAL_ALERT_DAYS = [90, 60, 30] as const;

export function renewalStage(endDate: Date, now = new Date()): 90 | 60 | 30 | 0 | null {
  const days = Math.ceil((endDate.getTime() - now.getTime()) / 86_400_000);
  if (days < 0) return 0;
  if (days <= 30) return 30;
  if (days <= 60) return 60;
  if (days <= 90) return 90;
  return null;
}

// ── Schémas ───────────────────────────────────────────────────────
const cents = z.number().int().min(0).max(1_000_000_000);
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date AAAA-MM-JJ');

export const contractInputSchema = z.object({
  plan: z.enum(TENANT_PLANS),
  status: z.enum(['DRAFT', 'ACTIVE', 'EXPIRED', 'TERMINATED']).default('DRAFT'),
  startDate: isoDate,
  endDate: isoDate,
  annualAmountCents: cents,
  billingFrequency: z.enum(['ANNUAL', 'SEMIANNUAL', 'QUARTERLY', 'MONTHLY']).default('ANNUAL'),
  paymentTermsDays: z.number().int().min(0).max(120).default(30),
  purchaseOrderNumber: z.string().max(80).optional().nullable(),
  autoRenew: z.boolean().default(false),
  maxUsers: z.number().int().min(1).max(10_000).optional().nullable(),
  maxReportsPerMonth: z.number().int().min(1).max(10_000_000).optional().nullable(),
  signedAt: z.string().datetime().optional().nullable(),
  signedByName: z.string().max(200).optional().nullable(),
  notes: z.string().max(5000).optional().nullable(),
}).refine((c) => c.endDate > c.startDate, { message: 'La fin du contrat doit suivre son début', path: ['endDate'] });
export type ContractInput = z.infer<typeof contractInputSchema>;

export const invoiceLineSchema = z.object({
  description: z.string().min(1).max(300),
  quantity: z.number().min(0.01).max(100_000),
  unitPriceCents: z.number().int().min(-100_000_000).max(100_000_000),
});

export const invoiceInputSchema = z.object({
  contractId: z.string().uuid().optional().nullable(),
  periodStart: isoDate.optional().nullable(),
  periodEnd: isoDate.optional().nullable(),
  purchaseOrderNumber: z.string().max(80).optional().nullable(),
  lines: z.array(invoiceLineSchema).min(1).max(50),
  notes: z.string().max(5000).optional().nullable(),
});
export type InvoiceInput = z.infer<typeof invoiceInputSchema>;

export const paymentInputSchema = z.object({
  amountCents: z.number().int().min(1).max(1_000_000_000),
  receivedAt: isoDate,
  method: z.enum(['CHEQUE', 'TRANSFER', 'EFT', 'CARD', 'OTHER']),
  reference: z.string().max(120).optional().nullable(),
  notes: z.string().max(2000).optional().nullable(),
});
export type PaymentInput = z.infer<typeof paymentInputSchema>;

/** Modifications d'une ville par le super admin (plan, statut, quotas, facturation) */
export const tenantAdminUpdateSchema = z.object({
  name: z.string().min(2).max(200).optional(),
  legalName: z.string().max(200).optional().nullable(),
  plan: z.enum(TENANT_PLANS).optional(),
  status: z.enum(TENANT_STATUSES).optional(),
  suspendedReason: z.string().max(1000).optional().nullable(),
  maxUsers: z.number().int().min(1).max(10_000).optional(),
  maxReportsPerMonth: z.number().int().min(1).max(10_000_000).nullable().optional(),
  trialEndsAt: z.string().datetime().nullable().optional(),
  currentPeriodEndsAt: z.string().datetime().nullable().optional(),
  contactEmail: z.string().email().max(320).nullable().optional(),
  contactPhone: z.string().max(50).nullable().optional(),
  websiteUrl: z.string().url().max(500).nullable().optional(),
  billingEmail: z.string().email().max(320).nullable().optional(),
  billingAddress: z.string().max(1000).nullable().optional(),
  billingContactName: z.string().max(200).nullable().optional(),
  taxExempt: z.boolean().optional(),
  populationEstimate: z.number().int().positive().nullable().optional(),
  publicPortalEnabled: z.boolean().optional(),
  require2fa: z.boolean().optional(),
});
export type TenantAdminUpdateInput = z.infer<typeof tenantAdminUpdateSchema>;

/** Ce que l'admin de la ville peut modifier lui-même */
export const tenantBrandingSchema = z.object({
  primaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
  secondaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
  logoKey: z.string().regex(/^uploads\/[0-9a-f-]{36}\/[0-9a-f-]{36}\.(jpg|png|webp)$/).nullable().optional(),
  contactEmail: z.string().email().max(320).nullable().optional(),
  contactPhone: z.string().max(50).nullable().optional(),
  websiteUrl: z.string().url().max(500).nullable().optional(),
  notificationsEmailEnabled: z.boolean().optional(),
  notificationsPushEnabled: z.boolean().optional(),
  publicPortalEnabled: z.boolean().optional(),
  require2fa: z.boolean().optional(),
});
export type TenantBrandingInput = z.infer<typeof tenantBrandingSchema>;
